import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import {
  addVehicleBindQuery,
  addVehicleFeatureQuery,
  fromAddVehicleFeaturesActions
} from '@amfam/policy/add-vehicle/data-access';
import { PolicySelectors as policyQuery } from '@amfam/policy/data-access';
import { vehicleQuery } from '@amfam/policy/vehicle/data-access';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { LoadingSpinnerService, Option } from '@amfam/ui-kit';

import * as VehicleInfoActions from '../+state/add-vehicle-feature.actions';
import * as addVehicleFeatureSelector from '../+state/add-vehicle-feature.selectors';

@Component({
  selector: 'ds-vehicle-garage-address',
  templateUrl: './vehicle-garage-address.component.html',
  styleUrls: ['../add-vehicle-container/add-vehicle-container.component.scss'],
  standalone: false
})
export class VehicleGarageAddressComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() cancelEvent = new EventEmitter<boolean>();
  @Output() errorEvent = new EventEmitter<unknown>();

  public vehicleRiskDescription$ = this.store.select(vehicleQuery.selectNewRiskDescription);
  public garageListOptions$ = this.buildGarageListOptions(
    this.store.select(addVehicleFeatureSelector.selectAdvancedAutoPolicyGarageLocation)
  );
  isPartner$ = this.store.select(BrandSelectors.selectIsPartner);
  public garageListForm: UntypedFormGroup;
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private store: Store,
    private formBuilder: UntypedFormBuilder,
    private spinnerService: LoadingSpinnerService,
    private copyService: CopyService
  ) {}

  ngOnInit(): void {
    this.spinnerService.stop();
    this.buildForm();
    this.handleError();
  }

  ngAfterViewInit() {
    this.store.dispatch(
      VehicleInfoActions.addVehicleSendAnalytics({
        payload: { pageAnalyticName: 'GarageAddress' }
      })
    );
  }

  buildForm() {
    this.garageListForm = this.formBuilder.group({
      garageOption: ['', Validators.compose([Validators.required])]
    });
  }

  handleError() {
    combineLatest([
      this.store.select(addVehicleFeatureQuery.selectNewVehicleFeaturesError),
      this.store.select(addVehicleBindQuery.selectVehicleANSNotificationError)
    ])
      .pipe(
        map(([featuresError, ansError]) => ({ featuresError, ansError })),
        takeUntil(this.stop$)
      )
      .subscribe(({ featuresError, ansError }) => {
        if (featuresError) {
          this.spinnerService.stop();
          this.store.dispatch(
            fromRouterActions.Go({
              path: ['policies/add-vehicle/agent-help']
            })
          );
        } else if (ansError) {
          this.errorEvent.emit();
          this.spinnerService.stop();
        }
      });
  }

  submitGarageAddressInfo() {
    this.spinnerService.start({
      blockActions: true
    });

    this.store.dispatch(
      VehicleInfoActions.setGarageInfo({
        garageAddress: this.garageListForm.controls['garageOption'].value.text
      })
    );

    this.store
      .select(policyQuery.selectAdvanceAutoPolicies)
      .pipe(
        withLatestFrom(
          this.store.select(addVehicleFeatureQuery.selectNewVehicleVin),
          this.store.select(addVehicleFeatureQuery.selectNewVehicleFeatures)
        ),
        take(1)
      )
      .subscribe(([advancedAutoPolicies, vin, features]) => {
        const autoPoliciesLength = _get(advancedAutoPolicies, 'length', 0);
        if (
          autoPoliciesLength > 1 &&
          this.garageListForm.controls['garageOption'].value.text ===
            this.copyService.getCopy('policy.addVehicleGarageSomewhereElseOptionText')
        ) {
          this.store.dispatch(
            fromRouterActions.Go({
              path: ['policies/add-vehicle/agent-help']
            })
          );
        } else {
          this.store.dispatch(
            fromAddVehicleFeaturesActions.getNewVehicleFeaturesSuccess({
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              payload: { request: { vin } as any, response: { features } as any }
            })
          );
        }
      });
  }

  buildGarageListOptions(addressList$: Observable<string[]>): Observable<Option[]> {
    const options: Option[] = [];
    let index = 0;

    return addressList$.pipe(
      filter(addressList => addressList.length > 0),
      take(1),
      map(addressList => {
        for (const address of addressList) {
          options.push({
            id: String(index++),
            text: address
          });
        }

        // AS: Add the last option as somewhere else
        options.push({
          id: String(index),
          text: this.copyService.getCopy('policy.addVehicleGarageSomewhereElseOptionText')
        });

        return options;
      })
    );
  }

  goBackOneStep() {
    this.store.dispatch(fromRouterActions.Back());
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }
}
